import "prismjs/components/prism-bash";
import "prismjs/components/prism-diff";
import "prismjs/components/prism-graphql";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-json";
import "prismjs/components/prism-jsx";
import "prismjs/components/prism-tsx";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-yaml";
import Aside from "./Aside";
import CtaLinkBtn from "./CtaLinkBtn";
import PathToInsight from "./PathToInsight";
import React, { ReactNode, useMemo } from "react";
import {
  Box,
  Button,
  ButtonProps,
  Heading,
  HeadingProps,
  Link,
  LinkProps,
  ListItem,
  ListItemProps,
  ListProps,
  OrderedList,
  Stack,
  StackProps,
  Text,
  TextProps,
  UnorderedList,
} from "@chakra-ui/react";
import { Cloudinary } from "./Cloudinary";
import {
  CodeBlock,
  EmbeddableExplorer,
  MultiCodeBlock,
} from "@apollo/chakra-helpers";
import { CollapsibleSection } from "./CollapsibleSection";
import { CustomLink } from "./CustomLink";
import { FiDownload } from "@react-icons/all-files/fi/FiDownload";
import { Footer } from "./Footer";
import { InlineCode } from "./InlineCode";
import { MDXProvider } from "@mdx-js/react";
import { NAV_HEIGHT_EYEBROW, NAV_HEIGHT_STYLES, Nav } from "./Nav";
import type { MDXComponents } from "mdx/types";

export const CONTAINER_MAX_WIDTH = "8xl";
export const LAYOUT_MIN_HEIGHT_STYLES = "75vh";

const components: MDXComponents = {
  wrapper: (props: StackProps) => (
    <Stack
      shouldWrapChildren
      fontSize={{ md: "lg" }}
      spacing="4"
      sx={{
        // [["h1", "h2", "h3", "h4", "h5", "h6"].toString()]: {
        //   pt: 6,
        // },
        blockquote: {
          borderLeftStyle: "solid",
          borderLeftWidth: "6px",
          borderLeftColor: "gray.300",
          background: "gray.50",
          py: 6,
          px: 4,
          my: 6,
          p: {
            m: 0,
          },
        },
        img: {
          padding: "40px",
        },
        ".chakra-heading": {
          scrollMarginTop: "180px",
        },
        ".token.comment": {
          fontWeight: "400!important",
        },
      }}
      {...props}
    />
  ),
  h1: (props: HeadingProps) => (
    <Heading
      as="h1"
      size="2xl"
      color={"blue.900"}
      fontWeight={"bold"}
      mb="4.5"
      {...props}
    />
  ),
  h2: (props: HeadingProps) => (
    <Heading
      as="h2"
      fontWeight={"bold"}
      color={"blue.900"}
      size="md"
      mb="16px"
      sx={{
        a: {
          color: "inherit",
        },
      }}
      {...props}
    />
  ),
  h3: (props: HeadingProps) => (
    <Heading
      as="h3"
      fontWeight={"bold"}
      color={"blue.900"}
      size="md"
      mb="16px"
      sx={{
        a: {
          color: "inherit",
        },
      }}
      {...props}
    />
  ),
  h4: (props: HeadingProps) => (
    <Heading
      as="h4"
      fontWeight={"bold"}
      color={"blue.900"}
      size="md"
      mb="2"
      {...props}
    />
  ),
  h5: (props: HeadingProps) => <Heading as="h5" size="sm" {...props} />,
  h6: (props: HeadingProps) => <Heading as="h5" size="xs" {...props} />,
  p: (props: TextProps) => <Text variant="bodySm" mb="24px" {...props} />,
  a: (props: LinkProps) => <CustomLink color="brand.500" {...props} />,
  ul: (props: ListProps) => <UnorderedList ml="10" mb="2" {...props} />,
  ol: (props: ListProps) => <OrderedList ml="10" mb="2" {...props} />,
  li: (props: ListItemProps) => {
    return (
      <ListItem
        color="gray.500"
        sx={{
          // selects all immediate children (>) of any tag (*), except for inline code
          "> *:not(code)": {
            marginBottom: "4",
          },
        }}
        {...props}
      />
    );
  },
  code: InlineCode,
  CodeBlock: (props) => (
    <Box my="6">
      <CodeBlock {...props} />
    </Box>
  ),
  MultiCodeBlock,
  EmbeddableExplorer: (props) => (
    <Box overflow="hidden" borderRadius={6} my="6">
      <EmbeddableExplorer
        height={500}
        showHeadersAndEnvVars={false}
        docsPanelState="closed"
        {...props}
      />
    </Box>
  ),
  DownloadButton: (props: ButtonProps) => (
    <Button as="a" target="_blank" leftIcon={<FiDownload />} {...props} />
  ),
  CollapsibleSection,
  CtaLinkBtn,
  Aside,
  Box,
  PathToInsight,
  Cloudinary: ({ scale = 80, ...props }) => (
    <Box m="0 auto" my="4" w={{ base: "100%", md: `${scale}%` }}>
      <Cloudinary {...props} />
    </Box>
  ),
  ExternalLink: ({ children, ...props }: LinkProps) => (
    <Link target="_blank" color="brand.500" {...props}>
      {children}
    </Link>
  ),
};

type LayoutProps = {
  children: ReactNode;
};

export default function Layout({ children }: LayoutProps) {
  const mdx = useMemo(
    () => <MDXProvider components={components}>{children}</MDXProvider>,
    [children]
  );

  return (
    <Box h={"100%"}>
      <Nav />
      <Box minH={LAYOUT_MIN_HEIGHT_STYLES} pt={NAV_HEIGHT_EYEBROW}>
        {mdx}
      </Box>
      <Footer />
    </Box>
  );
}
